import GUI from 'lil-gui';
import type { DistantSound } from './AudioSystem';

/** What the sandbox panel can make the game do. */
export type SandboxActions = {
  nights: number;
  startNight(index: number): void;
  setSafe(safe: boolean): void;
  setQuiet(quiet: boolean): void;
  call(): void;
  mapFlash(): void;
  unknownText(text: string): void;
  photo(): void;
  figure(): void;
  himAhead(): void;
  caught(): void;
  whisper(): void;
  extraStep(): void;
  distant(kind: DistantSound): void;
  lampStutter(): void;
};

/**
 * Dev-only panel for trying the scares on demand (dev server, or `?sandbox`).
 * The backquote key (`) shows and hides it.
 */
export class Sandbox {
  private gui: GUI | null = null;
  private readonly onKey = (e: KeyboardEvent): void => {
    if (e.code !== 'Backquote' || !this.gui) return;
    if (this.gui._hidden) this.gui.show();
    else this.gui.hide();
  };

  static wanted(): boolean {
    return import.meta.env.DEV || new URLSearchParams(location.search).has('sandbox');
  }

  constructor(a: SandboxActions) {
    const gui = new GUI({ title: 'Sandbox  ( ` hides )' });
    this.gui = gui;
    gui.domElement.style.zIndex = '60';
    // Starts folded to its title bar (open it by clicking), so it never covers the game's own buttons.
    gui.close();
    const state = { night: 5, safe: false, quiet: false, text: 'why did you stop?', sound: 'chime' as DistantSound };

    const run = gui.addFolder('Night');
    run.add(state, 'night', 1, a.nights, 1).name('night');
    const go = { go: (): void => undefined };
    run.add(go, 'go').name('▶ Play this night');
    const safe = run.add(state, 'safe').name("Can't be caught").onChange((v: boolean) => a.setSafe(v));
    const quiet = run.add(state, 'quiet').name('No Mom texts').onChange((v: boolean) => a.setQuiet(v));
    // Sandbox runs are for watching the scares: safe and quiet (untick either to play it straight).
    go.go = () => {
      a.startNight(state.night - 1);
      safe.setValue(true);
      quiet.setValue(true);
    };

    const phone = gui.addFolder('Phone scares');
    phone.add({ call: a.call }, 'call').name('Video call → his face');
    phone.add({ flash: a.mapFlash }, 'flash').name('Map flash (opens map)');
    phone.add(state, 'text').name('Unknown text');
    phone.add({ send: () => a.unknownText(state.text) }, 'send').name('Send text (linked phone)');
    phone.add({ photo: a.photo }, 'photo').name('Photo of her from behind');

    const eerie = gui.addFolder('Eerie');
    eerie.add({ figure: a.figure }, 'figure').name('Figure down the street');
    eerie.add({ him: a.himAhead }, 'him').name('Him standing ahead');
    eerie.add({ whisper: a.whisper }, 'whisper').name('Whisper behind her');
    eerie.add({ step: a.extraStep }, 'step').name('One step too many');
    eerie.add({ lamp: a.lampStutter }, 'lamp').name('Lamp stutter');
    eerie.add(state, 'sound', ['chime', 'howl', 'clang', 'slam', 'cry']).name('distant sound');
    eerie.add({ play: () => a.distant(state.sound) }, 'play').name('Play distant sound');

    gui.addFolder('Ending').add({ caught: a.caught }, 'caught').name('Get caught');

    window.addEventListener('keydown', this.onKey);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    this.gui?.destroy();
    this.gui = null;
  }
}
